<template>
    <v-container fluid class="pa-0">
        <div class="result-container">
            <div class="header">
                <h2>商品讲解记录（完整）</h2>
                <div class="stats">
                    <div class="stat-item">
                        <div class="stat-number">{{ totalRecords }}</div>
                        <div class="stat-label">总记录数</div>
                    </div>
                    <div class="stat-item">
                        <div class="stat-number">{{ recordedCount }}</div>
                        <div class="stat-label">已录制</div>
                    </div>
                    <div class="stat-item">
                        <div class="stat-number">{{ notRecordedCount }}</div>
                        <div class="stat-label">未录制</div>
                    </div>
                    <div class="stat-item">
                        <div class="stat-number">{{ recordingRate }}%</div>
                        <div class="stat-label">录制率</div>
                    </div>
                </div>
                
                <div class="control-buttons">
                    <v-btn @click="expandAll" color="primary" size="small">全部展开</v-btn>
                    <v-btn @click="collapseAll" color="grey" size="small" class="ml-2">全部收起</v-btn>
                </div>
            </div>
            
            <div class="content-container">
                <div v-for="(group, index) in groupedData" :key="index" class="group-item">
                    <div 
                        class="group-header"
                        :class="{ active: group.expanded }"
                        @click="toggleGroup(index)"
                    >
                        <div class="group-info">
                            <div class="group-title">{{ group.talentNickname }}</div>
                            <div class="product-name" v-html="group.productName"></div>
                            <div class="group-stats">
                                <span>共{{ group.segments ? group.segments.length : 0 }}个片段</span>
                                <span class="stat-badge recorded">{{ group.recordedSegments }}已录制</span>
                                <span class="stat-badge not-recorded">{{ group.notRecordedSegments }}未录制</span>
                                <span>总时长: {{ group.totalDuration }}</span>
                            </div>
                        </div>
                        <div class="expand-icon" :class="{ expanded: group.expanded }">▶</div>
                    </div>
                    
                    <div class="group-content" :class="{ show: group.expanded }">
                        <v-data-table
                            :headers="segmentHeaders"
                            :items="group.segments"
                            :items-per-page="-1"
                            hide-default-footer
                            class="segment-table"
                        >
                            <template v-slot:[`item.segmentNum`]="{ index }">
                                片段 {{ index + 1 }}
                            </template>
                            
                            <template v-slot:[`item.讲解开始时间`]="{ item }">
                                <span class="datetime">{{ item['讲解开始时间'] }}</span>
                            </template>
                            
                            <template v-slot:[`item.讲解结束时间`]="{ item }">
                                <span class="datetime">{{ item['讲解结束时间'] }}</span>
                            </template>
                            
                            <template v-slot:[`item.讲解时长（分钟）`]="{ item }">
                                <span class="duration">{{ formatDuration(item['讲解时长（分钟）']) }}</span>
                            </template>
                            
                            <template v-slot:[`item.全网销量`]="{ item }">
                                <span class="sales">{{ formatSales(item['全网销量']) }}</span>
                            </template>
                            
                            <template v-slot:[`item.recorded_status`]="{ item }">
                                <span :class="item['已录制'] ? 'recorded-yes' : 'recorded-no'">
                                    {{ item['已录制'] ? '已录制' : '未录制' }}
                                </span>
                            </template>
                            
                            <template v-slot:[`item.actions`]="{ item }">
                                <v-btn 
                                    :disabled="!item['录制ID']" 
                                    @click="handleEdit(item['录制ID'], item['讲解开始时间'])"
                                    color="primary"
                                    size="small"
                                >
                                    剪辑
                                </v-btn>
                            </template>
                            
                            <template v-slot:[`item.商品名称`]="{ item }">
                                <span v-html="item['商品名称']"></span>
                            </template>
                        </v-data-table>
                    </div>
                </div>
            </div>
        </div>
    </v-container>
</template>

<script setup>
import { ref, computed, defineProps, watch } from 'vue';
import { useRouter } from 'vue-router';

const props = defineProps({
    commodity_details: Array
})

const router = useRouter();

const segmentHeaders = [
    { title: '片段', key: 'segmentNum', sortable: false, width: '80px' },
    { title: '讲解开始时间', key: '讲解开始时间', sortable: true, width: '140px' },
    { title: '讲解结束时间', key: '讲解结束时间', sortable: true, width: '140px' },
    { title: '讲解时长', key: '讲解时长（分钟）', sortable: true, width: '100px' },
    { title: '商品信息', key: '商品名称', sortable: false },
    { title: '全网销量', key: '全网销量', sortable: true, width: '120px' },
    { title: '录制状态', key: 'recorded_status', sortable: true, width: '100px' },
    { title: '操作', key: 'actions', sortable: false, width: '80px' }
];

// 统计数据
const totalRecords = computed(() => props.commodity_details?.length || 0);
const recordedCount = computed(() => {
    return props.commodity_details?.filter(item => item['已录制']).length || 0;
});
const notRecordedCount = computed(() => totalRecords.value - recordedCount.value);
const recordingRate = computed(() => {
    return totalRecords.value > 0 ? Math.round((recordedCount.value / totalRecords.value) * 100) : 0;
});

// 使用 ref 来存储分组数据，使其可以响应式更新
const groupedData = ref([]);

// 监听 props 变化并更新分组数据
const updateGroupedData = () => {
    if (!props.commodity_details || props.commodity_details.length === 0) {
        groupedData.value = [];
        return;
    }
    
    const groups = new Map();
    
    props.commodity_details.forEach(item => {
        const key = `${item['达人昵称'] || item.talent_nickname || '未知达人'}_${item['商品名称'] || item.current_commodity || '未知商品'}`;
        
        if (!groups.has(key)) {
            groups.set(key, {
                talentNickname: item['达人昵称'] || item.talent_nickname || '未知达人',
                productName: item['商品名称'] || item.current_commodity || '未知商品',
                segments: [],
                expanded: false
            });
        }
        
        groups.get(key).segments.push(item);
    });
    
    // 为每个组计算统计信息
    groupedData.value = Array.from(groups.values()).map(group => {
        const recordedSegments = group.segments.filter(s => s['已录制']).length;
        const notRecordedSegments = (group.segments ? group.segments.length : 0) - recordedSegments;
        const totalDuration = group.segments.reduce((sum, segment) => {
            const duration = parseFloat(segment['讲解时长（分钟）'] || 0) || 0;
            return sum + duration;
        }, 0);
        
        return {
            ...group,
            recordedSegments,
            notRecordedSegments,
            totalDuration: totalDuration > 0 ? `${totalDuration.toFixed(1)}分钟` : '-'
        };
    });
};

// 监听 props 变化
watch(() => props.commodity_details, updateGroupedData, { immediate: true });

// 展开/收起功能
const toggleGroup = (index) => {
    groupedData.value[index].expanded = !groupedData.value[index].expanded;
};

const expandAll = () => {
    groupedData.value.forEach(group => {
        group.expanded = true;
    });
};

const collapseAll = () => {
    groupedData.value.forEach(group => {
        group.expanded = false;
    });
};

// 格式化函数
// const formatDateTime = (dateTime) => {
//     if (!dateTime) return '-';
//     try {
//         const date = new Date(dateTime);
//         return date.toLocaleString('zh-CN', {
//             timeZone: 'UTC',  // 使用UTC时区
//             month: '2-digit',
//             day: '2-digit',
//             hour: '2-digit',
//             minute: '2-digit',
//             second: '2-digit',
//             hour12: false  // 确保使用24小时制
//         });
//     } catch (e) {
//         return dateTime;
//     }
// };

const formatDuration = (duration) => {
    if (!duration) return '-';
    const num = parseFloat(duration);
    return isNaN(num) ? '-' : `${num.toFixed(1)}分钟`;
};

const formatSales = (sales) => {
    if (!sales) return '-';
    return sales; // 直接返回原始字符串值，不做数字处理
};

// 剪辑按钮处理
const handleEdit = (transcription_id, explain_t) => {
    const baseHref = router.resolve('kol/para').href;
    const hrefWithParam = `${baseHref}?tid=${transcription_id}&jtime=${explain_t}`;

    const link = document.createElement('a');
    link.href = hrefWithParam;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};
</script>

<style scoped>
.result-container {
    width: 100%;
    min-height: 100vh;
    background-color: #f5f5f5;
    padding: 20px;
}

.header {
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
    padding: 20px;
    text-align: center;
    margin-bottom: 20px;
}

.header h2 {
    color: #333;
    margin-bottom: 20px;
    font-size: 24px;
}

.stats {
    display: flex;
    justify-content: center;
    gap: 30px;
    margin-bottom: 20px;
    flex-wrap: wrap;
}

.stat-item {
    text-align: center;
    padding: 15px 25px;
    background-color: #f8f9fa;
    border-radius: 8px;
    min-width: 120px;
}

.stat-number {
    font-size: 28px;
    font-weight: bold;
    color: #007bff;
}

.stat-label {
    font-size: 14px;
    color: #666;
    margin-top: 5px;
}

.control-buttons {
    margin-top: 20px;
}

.content-container {
    margin-top: 20px;
}

.group-item {
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    margin-bottom: 15px;
    background-color: white;
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.group-header {
    padding: 20px;
    background-color: #f8f9fa;
    border-radius: 8px 8px 0 0;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    transition: all 0.3s ease;
}

.group-header:hover {
    background-color: #e9ecef;
    transform: translateY(-1px);
}

.group-header.active {
    background-color: #007bff;
    color: white;
}

.group-info {
    display: flex;
    align-items: center;
    gap: 20px;
    flex: 1;
    flex-wrap: wrap;
}

.group-title {
    font-weight: bold;
    font-size: 18px;
    min-width: 120px;
    color: #2c3e50;
}

.group-header.active .group-title {
    color: white;
}

.product-name {
    max-width: 400px;
    word-wrap: break-word;
    font-weight: normal;
    flex: 1;
    font-size: 16px;
    color: #34495e;
}

.group-header.active .product-name {
    color: rgba(255, 255, 255, 0.9);
}

.group-stats {
    display: flex;
    gap: 15px;
    font-size: 13px;
    color: #666;
    flex-wrap: wrap;
}

.group-header.active .group-stats {
    color: rgba(255, 255, 255, 0.8);
}

.expand-icon {
    font-size: 20px;
    transition: transform 0.3s ease;
    color: #007bff;
}

.group-header.active .expand-icon {
    color: white;
}

.expand-icon.expanded {
    transform: rotate(90deg);
}

.group-content {
    display: none;
    border-top: 1px solid #e0e0e0;
    animation: fadeIn 0.3s ease;
}

.group-content.show {
    display: block;
}

@keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
}

.stat-badge {
    background-color: #007bff;
    color: white;
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 11px;
    white-space: nowrap;
}

.stat-badge.recorded {
    background-color: #28a745;
}

.stat-badge.not-recorded {
    background-color: #dc3545;
}

.recorded-yes {
    color: #28a745;
    font-weight: bold;
}

.recorded-no {
    color: #dc3545;
    font-weight: bold;
}

.datetime {
    white-space: nowrap;
    font-size: 13px;
    color: #555;
}

.duration {
    text-align: center;
    color: #007bff;
    font-weight: 500;
}

.sales {
    text-align: right;
    font-weight: bold;
    color: #28a745;
}

:deep(.segment-table) {
    background-color: transparent;
}

:deep(.segment-table .v-data-table__wrapper) {
    font-size: 14px;
}

:deep(.segment-table .v-data-table-header th) {
    background-color: #fafafa !important;
    font-weight: bold !important;
    color: #333 !important;
    font-size: 13px !important;
    padding: 16px !important;
}

:deep(.segment-table .v-data-table__tr) {
    transition: background-color 0.2s;
}

:deep(.segment-table .v-data-table__tr:hover) {
    background-color: #f8f9fa !important;
}

:deep(.segment-table .v-data-table__td) {
    padding: 12px 16px !important;
}

/* mark 标签样式 */
:deep(mark) {
    background-color: yellow !important;
    color: #000 !important;
    padding: 2px 4px;
    border-radius: 3px;
}

/* 分组头部的 mark 标签样式 */
.product-name :deep(mark) {
    background-color: yellow !important;
    color: #000 !important;
    padding: 2px 4px;
    border-radius: 3px;
}

/* 表格中的 mark 标签样式 */
:deep(.segment-table mark) {
    background-color: yellow !important;
    color: #000 !important;
    padding: 2px 4px;
    border-radius: 3px;
}

/* 响应式设计 */
@media (max-width: 768px) {
    .stats {
        gap: 15px;
    }
    
    .stat-item {
        padding: 10px 15px;
        min-width: 100px;
    }
    
    .stat-number {
        font-size: 22px;
    }
    
    .group-info {
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;
    }
    
    .group-title {
        min-width: auto;
    }
    
    .product-name {
        max-width: 100%;
    }
    
    .group-stats {
        gap: 10px;
        font-size: 12px;
    }
}
</style>
